import { TextInput, Text } from "@mantine/core"
import { IconCheck } from "@tabler/icons-react"
import {
  AccessorKeyColumnDef,
  ColumnDef,
  createColumnHelper,
} from "@tanstack/react-table"
import { Button, getCheckboxColumnDefs, TextCell } from "@tfg/ui"
import { ChangeEvent, useCallback, useMemo, useState } from "react"

import { LanguageIso6391 } from "@tfg/types/constants/language.constants"
import { AdminResponses } from "@tfg/types/endpoints/admin"

import { useUpdateTranslationsMutation } from "./hooks"

type Props = {
  defaultValue?: string
  onSave: (newValue: string) => void
}

export function EditableCellWithTextInput({
  defaultValue = "",
  onSave,
}: Props) {
  const [inputValue, setInputValue] = useState(defaultValue)
  const [isChanged, setIsChanged] = useState(false)

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value)
    setIsChanged(event.target.value !== defaultValue)
  }

  const handleSave = () => {
    onSave(inputValue)
    setIsChanged(false)
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
      }}
    >
      <TextInput
        value={inputValue}
        onChange={handleInputChange}
        style={{ flex: 1 }}
      />
      {isChanged && (
        <Button
          size="xs"
          onClick={handleSave}
          style={{
            marginLeft: "8px",
          }}
        >
          <IconCheck size={16} strokeWidth={2} />
        </Button>
      )}
    </div>
  )
}

const columnHelper = createColumnHelper<AdminResponses.Translation>()

type LanguageAccessor = (
  language: LanguageIso6391
) => AccessorKeyColumnDef<AdminResponses.Translation, string | undefined>

const useLanguageAccessor = (): LanguageAccessor => {
  const { mutate: updateTranslation } = useUpdateTranslationsMutation()

  const languageAccessor: LanguageAccessor = useCallback(
    (
      language: LanguageIso6391
    ): AccessorKeyColumnDef<AdminResponses.Translation, string | undefined> => {
      return columnHelper.accessor(language, {
        header: () => (
          <Text fw={600} ta="center">
            {language}
          </Text>
        ),
        enableSorting: false,
        id: language,
        size: 200,
        cell: ({ row, getValue }) => {
          const value = getValue()
          return (
            <EditableCellWithTextInput
              key={row.original.id}
              defaultValue={value}
              onSave={(newValue: string) => {
                updateTranslation({
                  id: row.original.id,
                  translatedLanguage: language,
                  translation: newValue,
                })
              }}
            />
          )
        },
      })
    },
    [updateTranslation]
  )

  return languageAccessor
}

export const useColumns = () => {
  const languageAccessor = useLanguageAccessor()

  const languages = useMemo(() => {
    return [
      LanguageIso6391.EN,
      LanguageIso6391.FR,
      LanguageIso6391.ZH,
      LanguageIso6391.PT,
      LanguageIso6391.IT,
      LanguageIso6391.ES,
      LanguageIso6391.TR,
      LanguageIso6391.AR,
      LanguageIso6391.KO,
      LanguageIso6391.JA,
      LanguageIso6391.BE,
      LanguageIso6391.RO,
      LanguageIso6391.LT,
    ]
  }, [])

  return useMemo<ColumnDef<AdminResponses.Translation, any>[]>(
    () => [
      getCheckboxColumnDefs<AdminResponses.Translation>(),
      columnHelper.accessor("id", {
        header: "ID",
        cell: ({ row: { original: translation } }) => (
          <TextCell value={translation.id.toString()} />
        ),
      }),
      columnHelper.accessor("originalText", {
        header: "Key",
        cell: ({ row: { original: translation } }) => (
          <TextCell value={translation.originalText} />
        ),
      }),
      ...languages.map(languageAccessor),
    ],
    [languageAccessor, languages]
  )
}
